import { useEffect } from 'react';
import { connectChatSocket } from '@/api/miniapp/chatSocket';
import { useAiChatStore } from '@/store/aiChatStore';

/**
 * Поддержание диалога в живом состоянии: открытие чата и доставка ответов.
 *
 * Вынесено из экрана: там это три эффекта с таймерами и сокетом, между
 * которыми легко потерять уборку. Экран остаётся про разметку.
 */

/**
 * Запасной опрос истории.
 *
 * Всё, что делает агент, приходит по сокету, поэтому при живом канале опрос
 * не нужен вовсе. Он остаётся только на случай, когда сокет недоступен —
 * например, прокси не пропускает upgrade.
 */
const HISTORY_REFRESH_MS = 15000;

/**
 * Первые секунды после открытия проверяются чаще.
 *
 * Часть заданий агент начинает сам, и его первый вопрос приходит с задержкой.
 * Обычный опрос раз в 15 секунд слишком редкий для начала диалога: пустой
 * экран в это время читается как зависшее приложение.
 */
const OPENING_NUDGES_MS = [3000, 8000];

/**
 * Открытие чата по заданию.
 *
 * Чат живёт на сервере: активный диалог один и переживает закрытие
 * приложения, поэтому экран всегда вызывает `open` — сервер сам решает,
 * создать чат или вернуть текущий.
 */
export function useChatSession(assignmentId: string, variantId?: string): void {
  const open = useAiChatStore((state) => state.open);
  const reset = useAiChatStore((state) => state.reset);
  const loadHistory = useAiChatStore((state) => state.loadHistory);

  useEffect(() => {
    if (!assignmentId) return undefined;

    let cancelled = false;
    const timers: number[] = [];

    void open(assignmentId, variantId).then((ok) => {
      if (!ok || cancelled) return;
      for (const delay of OPENING_NUDGES_MS) {
        timers.push(
          window.setTimeout(() => {
            if (!cancelled && useAiChatStore.getState().messages.length === 0) void loadHistory();
          }, delay)
        );
      }
    });

    // Смена задания закрывает предыдущий диалог на экране, но не на сервере
    return () => {
      cancelled = true;
      for (const timer of timers) window.clearTimeout(timer);
      reset();
    };
  }, [assignmentId, variantId, open, reset, loadHistory]);
}

/**
 * Доставка ответов ассистента: канал сообщений и запасной опрос.
 *
 * Ответы агента и итоги задач приходят асинхронно, и без этого материал,
 * готовый уже после ожидания, не появился бы на экране до следующего действия
 * пользователя.
 */
export function useChatLiveUpdates(finished: boolean): void {
  const loadHistory = useAiChatStore((state) => state.loadHistory);
  const applySocketMessage = useAiChatStore((state) => state.applySocketMessage);
  const setSocketAlive = useAiChatStore((state) => state.setSocketAlive);
  const socketAlive = useAiChatStore((state) => state.socketAlive);
  const awaitingReply = useAiChatStore((state) => state.awaitingReply);
  const runPending = useAiChatStore((state) => state.runPending);

  useEffect(() => {
    if (finished) return undefined;

    const socket = connectChatSocket({
      // Текст ответа приходит в самом сообщении: запрашивать историю ради него
      // не нужно. За файлом стор сходит сам — его адрес задаёт история
      onMessage: (message) => applySocketMessage(message),
      onReady: (reconnected) => {
        // После разрыва часть сообщений могла не дойти
        if (reconnected) void loadHistory();
      },
      onAliveChange: setSocketAlive,
    });

    return () => socket.close();
  }, [finished, loadHistory, applySocketMessage, setSocketAlive]);

  useEffect(() => {
    if (finished) return undefined;

    // Пока канал ответов жив, история не опрашивается: сокет приносит и текст,
    // и статус, а лишние запросы только нагружают сервер. Исключение — время
    // ожидания ответа: сокет на пользователя один, и соединение из другого
    // клиента забирает сообщение себе. Без опроса ответ не появился бы вовсе
    const waiting = awaitingReply || runPending;
    if (socketAlive && !waiting) return undefined;

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadHistory();
    }, HISTORY_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [finished, loadHistory, socketAlive, awaitingReply, runPending]);
}
